import type {
  CommentItem,
  Paginated,
  VideoCheckPatchRequest,
  VideoCheckRequest,
  VideoCheckResponse,
  VideoDetail,
  VideoListItem,
  VideoListQuery,
  VideoModerateRequest,
  VideoQuickCounts,
  VideoStageCounts,
  VideoVerifyLinkRequest,
} from '@ayub/shared'
import { apiGet, apiPatch, apiPost } from '@/lib/api'

/** `docs/04-ui-ux.md` "Jadval (List ko'rinish)" — sahifada 50 ta, xuddi blogerlar kabi. */
export const VIDEOS_PAGE_SIZE = 50

/**
 * `GET /api/videos` javobi — `components/bloggers/api.ts`dagi naqshning aynan o'zi
 * (`Paginated<T> & { ...hisoblagichlar }`): sahifalangan ro'yxat + FILTRLARDAN mustaqil
 * hisoblagichlar. Bu yerda ikkitasi bor — bosqich bo'yicha (`stageCounts`, kanban ustun
 * sarlavhalari) va tez filtr bo'yicha (`quickCounts`, "Kechikkan/Bugun/To'lovga tayyor/
 * Tekshirilmagan havolalar").
 *
 * ⚠️ DIQQAT — TEKSHIRILMAGAN TAXMIN: server qismi (Bosqich 7 backend) shu WebApp bilan
 * PARALLEL quriladi. Bu shakl `packages/shared/src/api.ts`dagi `VideoStageCounts` /
 * `VideoQuickCounts` tiplaridan va blogerlar endpointining ALLAQACHON ISHLAYOTGAN naqshidan
 * KELTIRIB CHIQARILGAN — lekin haqiqiy serverga qarshi hali TEKSHIRILMAGAN. Agar server bu
 * ikki hisoblagichni boshqacha joyda (masalan alohida `/api/videos/counts` endpointida)
 * qaytarsa, FAQAT shu fayl (va pastdagi `fetchVideos`) yangilanishi kifoya — qolgan UI kodi
 * (`hooks.ts` orqali) bu tafsilotdan mustaqil.
 */
export interface VideoListResponse extends Paginated<VideoListItem> {
  stageCounts: VideoStageCounts
  quickCounts: VideoQuickCounts
}

function buildQueryString(query: VideoListQuery): string {
  const params = new URLSearchParams()
  if (query.stage) params.set('stage', query.stage)
  if (query.due) params.set('due', query.due)
  if (query.q) params.set('q', query.q)
  if (query.bloggerId) params.set('bloggerId', String(query.bloggerId))
  if (query.page) params.set('page', String(query.page))
  if (query.pageSize) params.set('pageSize', String(query.pageSize))
  const qs = params.toString()
  return qs.length > 0 ? `?${qs}` : ''
}

export function fetchVideos(query: VideoListQuery): Promise<VideoListResponse> {
  return apiGet<VideoListResponse>(`/videos${buildQueryString(query)}`)
}

/** ⚠️ Yo'l taxmin qilingan — `components/bloggers/api.ts`dagi `/bloggers/:id` naqshiga mos. */
export function fetchVideo(id: number): Promise<VideoDetail> {
  return apiGet<VideoDetail>(`/videos/${id}`)
}

/** ⚠️ Yo'l taxmin qilingan — kontrakt (`VideoModerateRequest`) FIKS, faqat yo'lning o'zi taxmin. */
export function moderateVideo(id: number, body: VideoModerateRequest): Promise<VideoDetail> {
  return apiPost<VideoDetail>(`/videos/${id}/moderate`, body)
}

/** ⚠️ Yo'l taxmin qilingan — `components/bloggers/BloggerDetail.tsx`dagi izoh naqshiga mos. */
export function addVideoComment(id: number, text: string): Promise<CommentItem> {
  return apiPost<CommentItem>(`/videos/${id}/comments`, { text })
}

/** Havola tekshiruvi (Bosqich 8) — kontrakt (`VideoVerifyLinkRequest`) FIKS, yo'l `moderate` bilan bir xil naqsh. */
export function verifyVideoLink(id: number, body: VideoVerifyLinkRequest): Promise<VideoDetail> {
  return apiPost<VideoDetail>(`/videos/${id}/verify-link`, body)
}

/**
 * Prosmotr kiritish (Bosqich 9, `docs/06-implementation-plan.md`) — kontrakt
 * (`VideoCheckRequest`/`VideoCheckResponse`, `packages/shared/src/api.ts`) FIKS, yo'l
 * `moderate`/`verify-link` bilan bir xil taxmin qilingan naqsh.
 *
 * ⚠️ Server tomoni (`POST /api/videos/:id/checks`) shu WebApp bilan PARALLEL quriladi va
 * yozilgan vaqtda hali `apps/server`da mavjud emas edi — bu funksiya haqiqiy endpointga
 * qarshi TEKSHIRILMAGAN, faqat shartnoma tipiga mos yozilgan.
 */
export function submitVideoCheck(id: number, body: VideoCheckRequest): Promise<VideoCheckResponse> {
  return apiPost<VideoCheckResponse>(`/videos/${id}/checks`, body)
}

/**
 * Kiritilgan prosmotrni tuzatish — kontrakt (`VideoCheckPatchRequest`) FIKS. Video uchun
 * yagona tekshiruv, shuning uchun yo'lda `checkNo` yo'q (`PATCH /videos/:id/check`).
 */
export function patchVideoCheck(
  id: number,
  body: VideoCheckPatchRequest,
): Promise<VideoCheckResponse> {
  return apiPatch<VideoCheckResponse>(`/videos/${id}/check`, body)
}
